/**
 * Store 路径解析与读写工具
 * 与 original UseStore 一致：支持 dataSet 数组下标（如 list[0]）、dataSetField 多级路径（如 pagination.current、value[0].name）
 */

export type PathSegment = { type: 'array' | 'property'; key: string | number };

/**
 * 解析 dataSet 名中的数组下标
 * 例如 "to_deal_lst[0]" → { baseName: "to_deal_lst", index: 0 }
 * "sessionId_list" → { baseName: "sessionId_list", index: null }
 */
export function parseArrayNotation(dataSetName: string): {
  baseName: string;
  index: number | null;
} {
  const match = dataSetName.match(/^(.+)\[(\d+)\]$/);
  if (match) {
    return {
      baseName: match[1],
      index: parseInt(match[2], 10),
    };
  }
  return {
    baseName: dataSetName,
    index: null,
  };
}

/**
 * 解析多层级路径为段列表
 * 例如 "pagination.current" → [{ type: 'property', key: 'pagination' }, { type: 'property', key: 'current' }]
 * "value[0].name" → [{ type: 'property', key: 'value' }, { type: 'array', key: 0 }, { type: 'property', key: 'name' }]
 */
export function parseNestedPath(path: string): PathSegment[] {
  if (!path || typeof path !== 'string') return [];

  const result: PathSegment[] = [];
  const parts = path
    .split('.')
    .flatMap((part) => {
      const arrayAccessMatch = part.match(/^([^\[]+)(\[\d+\])(.*)$/);
      if (arrayAccessMatch) {
        const list = [arrayAccessMatch[1]];
        let remaining = arrayAccessMatch[2] + arrayAccessMatch[3];
        let m: RegExpMatchArray | null;
        while ((m = remaining.match(/^\[(\d+)\](.*)$/))) {
          list.push(`[${m[1]}]`);
          remaining = m[2];
        }
        if (remaining) list.push(remaining);
        return list;
      }
      return [part];
    });

  for (const part of parts) {
    if (part.startsWith('[') && part.endsWith(']')) {
      const index = parseInt(part.slice(1, -1), 10);
      if (!Number.isNaN(index)) result.push({ type: 'array', key: index });
    } else if (part) {
      result.push({ type: 'property', key: part });
    }
  }

  return result;
}

/**
 * 按路径安全获取嵌套值
 */
export function getNestedValue(
  obj: any,
  path: PathSegment[]
): any {
  if (path.length === 0) return obj;

  let current: any = obj;
  for (const segment of path) {
    if (current === undefined || current === null) return undefined;
    if (segment.type === 'array') {
      if (!Array.isArray(current)) return undefined;
      current = current[segment.key as number];
    } else {
      current = current[segment.key];
    }
  }
  return current;
}

/**
 * 按路径安全设置嵌套值，返回新对象/数组（不修改原对象）
 */
export function setNestedValue(
  obj: any,
  path: PathSegment[],
  value: any
): any {
  if (!path.length) return value;

  // 浅拷贝根节点
  const result = Array.isArray(obj) ? [...obj] : { ...obj };
  let current: any = result;

  // 遍历中间层：每层都做浅拷贝，保证不会修改被 immer freeze 的旧对象
  for (let i = 0; i < path.length - 1; i++) {
    const segment = path[i];
    const nextSegment = path[i + 1];
    const key = segment.key;

    let child = current[key];
    if (child === undefined || child === null) {
      child = nextSegment.type === 'array' ? [] : {};
    } else if (nextSegment.type === 'array' && !Array.isArray(child)) {
      child = [];
    } else if (nextSegment.type === 'property' && Array.isArray(child)) {
      child = Object.fromEntries(
        (child as any[]).map((v, idx) => [String(idx), v]).filter(([, v]) => v !== undefined)
      );
    } else {
      // 关键：浅拷贝中间节点，避免修改 frozen 对象
      child = Array.isArray(child) ? [...child] : { ...child };
    }
    current[key] = child;
    current = child;
  }

  // 处理最后一个 segment：直接赋值（current 已是拷贝，可安全写入）
  const lastSegment = path[path.length - 1];
  current[lastSegment.key] = value;

  return result;
}
